import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import {
  catchError,
  from,
  map,
  Observable,
  of, retry,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';

import { ERROR_PAGE_URL } from '../../pages/not-found-error/not-found-error.page';
import { TemplateSampleService } from '../../services/template-sample.service';
import { handleHttpError } from '../../utils/api';
import { TemplateSampleActions } from '../actions/template-sample.actions';
import {
  selectActiveOrganisationId,
} from '../selectors/user.selector';

@Injectable()
export class TemplateSampleEffect {
  public get$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateSampleActions.loadSamples),
      withLatestFrom(this.store.select(selectActiveOrganisationId)),
      switchMap(([{ templateId }, organisationId]) =>
        from(this.service.get(organisationId, templateId)).pipe(
          map((samples) => TemplateSampleActions.loadSamplesSuccess({ samples: samples.items })),
          catchError((error) => of(TemplateSampleActions.loadSamplesFailure({ error }))),
        ),
      ),
    ),
  );

  public getFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TemplateSampleActions.loadSamplesFailure),
        tap(async () => await this.router.navigateByUrl(`/${ERROR_PAGE_URL}`)),
      ),
    { dispatch: false },
  );

  public createSample$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateSampleActions.createSample),
      withLatestFrom(this.store.select(selectActiveOrganisationId)),
      switchMap(([action, organisationId]) => {
        return from(
          this.service.create(
            organisationId,
            action.templateId,
            action.sample,
          ),
        ).pipe(
          map((sample) =>
            TemplateSampleActions.createSampleSuccess({
              sample,
            }),
          ),
          catchError((error) =>
            of(
              TemplateSampleActions.createSampleFailure({
                error,
                sample: {
                  client: organisationId,
                  templateId: action.templateId,
                  ...action.sample,
                },
              }),
            ),
          ),
        );
      }),
    ),
  );

  public loadSample$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateSampleActions.loadSample),
      withLatestFrom(this.store.select(selectActiveOrganisationId)),
      switchMap(([action, organisationId]) =>
        from(
          this.service.getOne(organisationId, action.templateId, action.sampleId),
        ).pipe(
          retry(2),
          map((sample) =>
            TemplateSampleActions.loadSampleSuccess({
              sample,
            }),
          ),
          catchError((error) => of(TemplateSampleActions.loadSampleFailure({ error }))),
        ),
      ),
    ),
  );

  public loadSampleFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TemplateSampleActions.loadSampleFailure),
        tap(async () => await this.router.navigateByUrl(`/${ERROR_PAGE_URL}`)),
      ),
    { dispatch: false },
  );

  public createSampleFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateSampleActions.createSampleFailure),
      map((error) => handleHttpError(error)),
    ),
  );

  public updateSample$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateSampleActions.updateSample),
      withLatestFrom(this.store.select(selectActiveOrganisationId)),
      switchMap(([action, organisationId]) => {
        return from(
          this.service.update(
            organisationId,
            action.templateId,
            action.sampleId,
            {
              ...action.sample,
            },
          ),
        ).pipe(
          map((sample) =>
            TemplateSampleActions.updateSampleSuccess({
              sample,
            }),
          ),
          catchError((error) =>
            of(
              TemplateSampleActions.updateSampleFailure({
                error,
                sample: action.sample,
              }),
            ),
          ),
        );
      }),
    ),
  );

  public updateSampleFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateSampleActions.updateSampleFailure),
      map((error) => handleHttpError(error)),
    ),
  );

  public delete$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateSampleActions.delete),
      withLatestFrom(this.store.select(selectActiveOrganisationId)),
      switchMap(([action, organisationId]) =>
        from(
          this.service.delete(organisationId, action.templateId, action.sampleId),
        ).pipe(
          map((payload) => TemplateSampleActions.deleteSuccess({ id : payload.id })),
          catchError((error) =>
            of(
              TemplateSampleActions.deleteFailure({
                error,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public deleteFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateSampleActions.deleteFailure),
      map((error) => handleHttpError(error)),
    ),
  );

  public bulkImport$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateSampleActions.bulkImport),
      withLatestFrom(this.store.select(selectActiveOrganisationId)),
      switchMap(([action, organisationId]) =>
        from(
          this.service.bulkImport(organisationId, action.templateId, action.file),
        ).pipe(
          map((response) =>
            TemplateSampleActions.bulkImportSuccess({
              response,
            }),
          ),
          catchError((error) =>
            of(
              TemplateSampleActions.bulkImportFailure({
                error,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public bulkImportFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateSampleActions.bulkImportFailure),
      map((error) => handleHttpError(error)),
    ),
  );

  public constructor(
    private readonly actions$: Actions,
    private readonly service: TemplateSampleService,
    private readonly router: Router,
    private readonly store: Store,
  ) {}
}
